/**
 * The types of events that are emitted by the Eventing Service, on behalf of the Service Routing Component.
 */
export {
  ServiceInstanceEvent,
  ServiceRoutingErrorType
} from '@jarvis/jweb-core/dist/plugins/service-routing';

/**
 * Error codes for the Auth plugin
 */
export { AuthPluginErrorCode } from '@jarvis/jweb-core/dist/plugins/auth';

/*
  JWeb Types for Auth Plugin
*/

export enum TokenLifetimeRequirementType {
  /**
   * Requires a token that will attempt to refresh after X seconds since its `iat` time.
   *
   * Specifying `timeInterval > (exp - iat)` will be ignored and the component will refresh
   * when the token expires.
   */
  preferredMaximumSecondsSinceIssued = 'preferredMaximumSecondsSinceIssued',
  /**
   * Requires a token that will not expire for at least X seconds.
   *
   * Specifying `timeInterval >= (exp - iat)` will always trigger token refresh.
   */
  preferredMinimumSecondsUntilExpiration = 'preferredMinimumSecondsUntilExpiration'
}
export enum TokenType {
  /**
   * For user token
   */
  user = 'user',
  /**
   * For device token
   */
  device = 'device'
}

export enum UserInteractionEntryPoint {
  /**
   * User interaction should start on the sign in page
   */
  signIn = 'signIn',
  /**
   * User interaction should start on the create-account page
   */
  createAccount = 'createAccount'
}

export enum LogLevel {
  Debug = 'DEBUG',
  Warn = 'WARN',
  Verbose = 'VERBOSE',
  Error = 'ERROR',
  Info = 'INFO',
  Log = 'LOG',
  Trace = 'TRACE'
}

export enum AssetUnitType {
  /**
   * Used when filtering data for a mobile use case
   */
  mobile = 'mobile',
  /**
   * Used when filtering data for a desktop use case
   */
  desktop = 'desktop',
  /**
   * Used when filtering data for a solution use case
   */
  solution = 'solution'
}

// @TODO: Should remove this and use from the JWEB package directly
export enum ReservedValueStoreKeys {
  /**
   * Application Instance Id (UUID)
   */
  applicationInstanceId = 'ApplicationInstanceId',
  /**
   * The stratus user id
   * 'stratus_id' in the stratus access token
   */
  stratusUserId = 'StratusUserId',
  /**
   * The hpid user id
   * 'sub' in the stratus access token
   */
  hpIdUserId = 'HpIdUserId',
  /**
   * The wpid user id
   * 'wpid' in the stratus access token
   */
  wpIdUserId = 'WpIdUserId',
  /**
   * The user's program level
   *
   * - TODO: Add more detail
   */
  userProgramLevel = 'UserProgramLevel',
  /**
   * The currently selected printer UUID
   */
  selectedPrinterUuid = 'SelectedPrinterUUID',
  /**
   * The model number of the selected printer
   * For example, the selected printer in the carousel for HP Smart
   */
  selectedPrinterModelNumber = 'SelectedPrinterModelNumber',
  /**
   * Application Identifier Name
   * Example: 'com.hpi.cdm.testapp'
   */
  webApplicationName = 'WebApplicationName',
  /**
   * Application version, 3 dot notation.
   * Semantic Versioning is HIGHLY recommended, with 4 segment for a monotonically increasing buildnumber or yyyymmddhh
   * Example: '8.4.1.2'
   */
  webApplicationVersion = 'WebApplicationVersion',
  /**
   * Contents of the OneTrust OptanonConsent cookie with a urn prefix (URL decoded!!)
   * urn model used to allow for migration away from OneTrust in the future
   */
  webApplicationConsent = 'WebApplicationConsent',
  /**
   * Unique UUID for the visit, intended as a short term cooking tracking (tbd app specific)
   * Example being a uuid set to a session cookie with an idle timeout of 1hr
   * Ex: "123e4567-e89b-12d3-a456-426614174000"
   */
  visitUuid = 'VisitUuid',
  /**
   * Unique UUID for the visitor, intended as a long term cookie tracking (tbd onetrust?)
   * Example being the consentId uuid value from webAppConsent field
   * Example: "123e4567-e89b-12d3-a456-426614174000"
   */
  visitorUuid = 'VisitorUuid',
  /**
   * Cloud service correlation linking key
   */
  ucdeCorrelationId = 'UcdeCorrelationId',
  /**
   * (Windows) The Device UUID (BIOS UUID) pulled from System APIs.
   */
  pcDeviceUuid = 'PcDeviceUuid',
  /**
   * The serial number that is pulled from System APIs.
   */
  systemSerialNumber = 'SystemSerialNumber',
  /**
   * The tenant id
   * The tenant ID to request a new user token be bounded to
   */
  tenantId = 'TenantId'
}

/**
 * Indicates the state of a service instance
 */
export enum ServiceInstanceState {
  /**
   * Indicates that the service instance is in the process of launching.  This is the initial
   * state for a service instance
   */
  launching = 'launching',
  /**
   * Indicates that the service instance successfully launched and is still running
   */
  running = 'running',
  /**
   * Indicates that the service instance has ended and its container has closed.  This
   * is a terminal state.
   */
  closed = 'closed',
  /**
   * Indicates that the service instance failed to launch.  For example, services
   * that launch in a Jarvis WebView may fail to launch when the device is not connected
   * to a network.  This is a terminal state.
   */
  failedToLaunch = 'failedToLaunch'
}

export enum DataCollectionEventNames {
  /**
   * The event name that the data collection service listens for, to filter and send cdm events (such as simpleUi) to the jarvis telemetry service.
   */
  cdmEvent = 'com.hp.jarvis.datacollection.cdm.event',
  /**
   * The name of the event that clients can subscribe to to be informed of the result of internal data collection processing.
   */
  cdmEventStatus = 'com.hp.jarvis.datacollection.cdm.event.status'
}

export enum Stack {
  local = 0,
  dev = 1,
  pie = 2,
  staging = 3,
  production = 4,
  custom = 5
}
